import { useCallback, useMemo, useState } from 'react'

/**
 * Hook to manage collapsible column groups in pivot tables
 * @param {Array} columns - The full list of columns with group metadata
 * @param {boolean|string} defaultExpanded - false | true | 'first'
 * @param {boolean} enabled - Whether column grouping is enabled
 * @returns {Object} - { visibleColumns, collapsedGroups, toggleGroup, collapseAll, expandAll }
 */
export const useCollapsibleColumns = (
  columns,
  defaultExpanded = false,
  enabled = false
) => {
  const [userCollapsedGroups, setUserCollapsedGroups] = useState(null)

  const defaultCollapsedGroups = useMemo(() => {
    if (!enabled) return new Set()

    const groupColumns = columns.filter(c => c.isGroup)
    const allGroupIds = groupColumns.map(c => c.groupId)

    if (allGroupIds.length === 0) return new Set()

    if (defaultExpanded === false) {
      return new Set(allGroupIds)
    } else if (defaultExpanded === 'first') {
      const firstGroup = allGroupIds[0]
      return new Set(allGroupIds.filter(id => id !== firstGroup))
    } else {
      return new Set()
    }
  }, [columns, defaultExpanded, enabled])

  const collapsedGroups = userCollapsedGroups !== null ? userCollapsedGroups : defaultCollapsedGroups

  const toggleGroup = useCallback((groupId) => {
    setUserCollapsedGroups(prev => {
      const current = prev !== null ? prev : defaultCollapsedGroups
      const next = new Set(current)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }, [defaultCollapsedGroups])

  const collapseAll = useCallback(() => {
    const groupColumns = columns.filter(c => c.isGroup)
    const allGroupIds = groupColumns.map(c => c.groupId)
    setUserCollapsedGroups(new Set(allGroupIds))
  }, [columns])

  const expandAll = useCallback(() => {
    setUserCollapsedGroups(new Set())
  }, [])

  const visibleColumns = useMemo(() => {
    if (!enabled) {
      return columns
    }

    return columns.filter(column => {
      return !(column.parentGroupId && collapsedGroups.has(column.parentGroupId))
    })
  }, [columns, collapsedGroups, enabled])

  return {
    visibleColumns,
    collapsedGroups,
    toggleGroup,
    collapseAll,
    expandAll
  }
}
